import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { CreateOrEditContractorComponent } from '../create-or-edit-contractor/create-or-edit-contractor.component';
import { FetchContractorsListRequest, DeleteContractorRequest } from '../../../../../../../core/projects.actions';
import { contractorsListInfo } from '../../../../../../../core/projects.selectors';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';

@Component({
    selector: 'app-contractors-list',
    templateUrl: './contractors-list.component.html',
    styleUrls: ['./contractors-list.component.scss'],
    standalone: false
})
export class ContractorsListComponent implements OnInit, OnDestroy {
  projectId: string;

  displayedColumns: string[] = ['name', 'email', 'phone', 'actions'];
  dataSource: any[] = [];
  filteredDataSource: any[] = [];
  originalDataSource: any[] = [];
  selectedRow: any = null;
  totalRecords = 0;
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  currentPage = 1;
  itemsPerPage = 10;

  private readonly onDestroy = new Subject<void>();

  constructor(
    private projectsService: ProjectsService,
    private store: Store,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
      this.fetchContractorsList();
    }

    this.store
      .select(contractorsListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          if (data && data?.list?.length > 0) {
            const sortedList = [...data.list].sort((a, b) => {
              const aPrime = a.prime ? 1 : 0;
              const bPrime = b.prime ? 1 : 0;
              if (bPrime !== aPrime) return bPrime - aPrime;
              return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
            });
            this.originalDataSource = sortedList;
            this.filteredDataSource = [...this.originalDataSource];
            this.totalRecords = data.totalRecords || this.originalDataSource.length;
            this.paginateData(true);
          } else {
            this.originalDataSource = [];
            this.filteredDataSource = [];
            this.totalRecords = 0;
            this.dataSource = [];
          }
        }
      });
  }

  fetchContractorsList(): void {
    const payload = {
      projectId: this.projectId,
    };

    if (this.projectId) {
      this.store.dispatch(FetchContractorsListRequest({ payload }));
    }
  }

  rowClicked(row: any): void {
    this.selectedRow = row;

    this.projectsService.openRightPanel.next({
      component: CreateOrEditContractorComponent,
      data: {
        ...row,
        projectId: this.projectId,
      },
    });
  }

  create(): void {
    this.projectsService.openRightPanel.next({
      component: CreateOrEditContractorComponent,
      data: {
        projectId: this.projectId,
      },
    });
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);

    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  deleteContractor(row: any, event: Event): void {
    event.stopPropagation();
    const contractorName = row?.name || 'this contractor';
    const message = `Deleting <b>${contractorName}</b> cannot be undone.<br />Proceed to delete?`;
    const dialogData = new ConfirmDialogModel('Delete?', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const payload = {
          projectId: this.projectId,
          contractorId: row?.id || row?.uuid,
        };
        this.store.dispatch(DeleteContractorRequest({ payload }));
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
